import { useState, useEffect, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { PHONE, PHONE_LINK, FONT_BODY, NAVY, GOLD, isSpanishPath } from "../lib/constants";
import { findAlternate } from "../lib/localPages";

const NAV_LINKS = [
  { label: "Home", to: "/", es: { label: "Inicio", to: "/es" } },
  { label: "Services", to: "/services", es: { label: "Servicios", to: "/es#servicios" } },
  { label: "About Us", to: "/about", es: { label: "Nosotros" } },
  { label: "Gallery", to: "/gallery", es: { label: "Galería" } },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact", es: { label: "Contacto" } },
];

const useNavLinks = () => {
  const isEs = useIsSpanish();
  return NAV_LINKS.map(({ label, to, es }) => {
    const target = isEs && es?.to ? es.to : to;
    return { key: slug(label), label: isEs && es?.label ? es.label : label, to: target, hash: target.includes("#") };
  });
};

const isHomePath = (to) => to === "/" || to === "/es";

const slug = (label) => label.toLowerCase().replace(/\s/g, "-");

const ES_ESTIMATE = { label: "Cotización gratis", to: "/es/estimate" };
const useIsSpanish = () => isSpanishPath(useLocation().pathname);

const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);
  const onScroll = useCallback(() => setScrolled(window.scrollY > 30), [setScrolled]);
  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);
  return scrolled;
};

const DesktopLinks = () => {
  const links = useNavLinks();
  return (
  <div className="hidden md:flex items-center gap-8">
    {links.map(({ key, label, to, hash }) => (
      <NavLink
        key={key}
        to={to}
        end={isHomePath(to)}
        data-testid={`nav-link-${key}`}
        className="text-sm font-semibold transition-colors duration-200 hover:text-[#D4A843]"
        style={({ isActive }) => ({
          color: isActive && !hash ? GOLD : "#2D2D2D",
          fontFamily: FONT_BODY,
        })}
      >
        {label}
      </NavLink>
    ))}
  </div>
  );
};

const mobileLinkStyle = (hash) => ({ isActive }) => ({
  color: isActive && !hash ? GOLD : "#2D2D2D",
  borderColor: "rgba(27,58,107,0.08)",
  fontFamily: FONT_BODY,
});

const MobileNavLink = ({ label, to, testid, onNavigate, hash = false }) => (
  <NavLink
    to={to}
    end={isHomePath(to)}
    data-testid={testid}
    onClick={onNavigate}
    className="text-left text-sm font-semibold py-2.5 border-b"
    style={mobileLinkStyle(hash)}
  >
    {label}
  </NavLink>
);

const MobileMenu = ({ onNavigate }) => {
  const isEs = useIsSpanish();
  const links = useNavLinks();
  return (
  <div className="md:hidden bg-white border-t" style={{ borderColor: "rgba(27,58,107,0.1)" }}>
    <div className="px-4 py-4 flex flex-col gap-3">
      {links.map(({ key, label, to, hash }) => (
        <MobileNavLink key={key} label={label} to={to} hash={hash} testid={`mobile-nav-${key}`} onNavigate={onNavigate} />
      ))}
      {isEs && <MobileNavLink {...ES_ESTIMATE} testid="mobile-nav-cotizacion-gratis" onNavigate={onNavigate} />}
      <a
        href={PHONE_LINK}
        data-testid="mobile-call-link"
        className="flex items-center justify-center gap-2 py-3 font-bold text-sm mt-1"
        style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
      >
        <Phone size={15} />
        {PHONE}
      </a>
      <div className="flex justify-center pt-2" onClick={onNavigate}>
        <LangSwitcher />
      </div>
    </div>
  </div>
  );
};

const LangSwitcher = ({ className = "" }) => {
  const { pathname } = useLocation();
  const isEs = isSpanishPath(pathname);
  const [enTo, esTo] = findAlternate(pathname) || (isEs ? ["/", pathname] : [pathname, "/es"]);
  const linkStyle = (active) => ({
    color: active ? GOLD : "#6B7280",
    fontFamily: FONT_BODY,
    fontWeight: 700,
  });
  return (
    <div className={`flex items-center gap-1 text-xs ${className}`} data-testid="lang-switcher">
      <Link to={enTo} data-testid="lang-en" style={linkStyle(!isEs)} className="px-1 transition-colors hover:text-[#D4A843]">EN</Link>
      <span style={{ color: "rgba(27,58,107,0.25)" }}>|</span>
      <Link to={esTo} data-testid="lang-es" style={linkStyle(isEs)} className="px-1 transition-colors hover:text-[#D4A843]">ES</Link>
    </div>
  );
};

const NavCtas = ({ menuOpen, onToggleMenu }) => {
  const isEs = useIsSpanish();
  return (
  <div className="flex items-center gap-3">
    <LangSwitcher className="hidden md:flex" />
    <a
      href={PHONE_LINK}
      data-testid="nav-call-btn"
      className="hidden md:flex items-center gap-2 px-5 py-2.5 text-sm font-bold transition-all duration-300 hover:opacity-90 hover:shadow-md"
      style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
    >
      <Phone size={14} />
      {PHONE}
    </a>
    <Link
      to={isEs ? ES_ESTIMATE.to : "/contact"}
      data-testid="nav-quote-btn"
      className="hidden md:flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-2 transition-all duration-300 hover:bg-[#1B3A6B] hover:text-white"
      style={{ borderColor: NAVY, color: NAVY, fontFamily: FONT_BODY }}
    >
      {isEs ? ES_ESTIMATE.label : "Free Quote"}
    </Link>
    <button
      data-testid="mobile-menu-btn"
      onClick={onToggleMenu}
      className="md:hidden p-2"
      style={{ color: NAVY }}
      aria-label="Toggle menu"
    >
      {menuOpen ? <X size={24} /> : <Menu size={24} />}
    </button>
  </div>
  );
};

export const Navbar = () => {
  const scrolled = useScrolled();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(14px)",
        borderBottom: scrolled
          ? "1px solid rgba(27,58,107,0.15)"
          : "1px solid rgba(27,58,107,0.06)",
        boxShadow: scrolled ? "0 2px 20px rgba(27,58,107,0.08)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo-link" onClick={() => setMenuOpen(false)}>
          <Logo />
        </Link>
        <DesktopLinks />
        <NavCtas menuOpen={menuOpen} onToggleMenu={() => setMenuOpen(!menuOpen)} />
      </div>
      {menuOpen && <MobileMenu onNavigate={() => setMenuOpen(false)} />}
    </nav>
  );
};
